<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { NXGDSButton, NXGDSButtonSizes, NXGDSButtonVariants } from '@nsftx/seven-nxg-design-system'
import {
  DetailsTableFooterRowEmphasis,
  type DetailsTableColumn,
  type DetailsTableFooter,
  type DetailsTableFooterRow,
  type DetailsTableRow,
} from '@/types/SSDetailsTableProps'

const props = defineProps<{
  columns: DetailsTableColumn[]
  rows: DetailsTableRow[]
  footer?: DetailsTableFooter
}>()

const { t } = useI18n({ useScope: 'global' })

const isDetailsExpanded = ref(false)

const detailsToggleIcon = computed(() => (isDetailsExpanded.value ? 'arrow-up-a' : 'arrow-down-a'))

const hasFooter = computed(
  () => !!props.footer?.summaryRows?.length || !!props.footer?.expandableRows?.length,
)

const hasExpandableRows = computed(() => !!props.footer?.expandableRows?.length)

const getFooterRowClasses = (row: DetailsTableFooterRow) => ({
  'ss-details-table__total-row': true,
  'ss-details-table__total-row--emphasis-medium':
    row.emphasis === DetailsTableFooterRowEmphasis.medium,
  'ss-details-table__total-row--emphasis-regular':
    row.emphasis === DetailsTableFooterRowEmphasis.regular,
})

const getFooterItemClasses = (tone?: DetailsTableFooterRow['tone']) => ({
  [`ss-details-table__text-${tone}`]: Boolean(tone),
})

const getCellClasses = (column: DetailsTableColumn) => ({
  'ss-details-table__cell': true,
  [`ss-details-table__cell--${column.modifier}`]: Boolean(column.modifier),
  [`ss-details-table__cell--align-${column.align}`]: Boolean(column.align),
})

const getHeaderLabel = (column: DetailsTableColumn) => (column.labelKey ? t(column.labelKey) : '')

const toggleDetailsExpanded = () => {
  isDetailsExpanded.value = !isDetailsExpanded.value
}
</script>

<template>
  <table class="ss-details-table">
    <thead class="ss-details-table__thead">
      <tr class="ss-details-table__thead-row">
        <td v-for="column in columns" :key="column.key" :class="getCellClasses(column)">
          {{ getHeaderLabel(column) }}
        </td>
      </tr>
    </thead>
    <tbody class="ss-details-table__tbody">
      <tr v-for="(row, rowIndex) in rows" :key="rowIndex" class="ss-details-table__tbody-row">
        <td v-for="column in columns" :key="column.key" :class="getCellClasses(column)">
          <slot :name="`cell-${column.key}`" :value="row[column.key]" :row="row" :column="column">
            {{ row[column.key] }}
          </slot>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="hasFooter" class="ss-details-table__tfoot">
      <tr class="ss-details-table__tfoot-row">
        <td class="ss-details-table__cell" :colspan="columns.length">
          <div class="ss-details-table__total">
            <div
              v-for="(summaryRow, index) in footer?.summaryRows"
              :key="`summary-${index}`"
              :class="getFooterRowClasses(summaryRow)"
            >
              <div
                class="ss-details-table__total-row__item"
                :class="getFooterItemClasses(summaryRow.tone)"
              >
                {{ t(summaryRow.labelKey) }}
              </div>
              <div
                class="ss-details-table__total-row__item"
                :class="getFooterItemClasses(summaryRow.tone)"
              >
                {{ summaryRow.value }}
              </div>
            </div>
            <div v-if="hasExpandableRows" class="ss-details-table__total-toggle">
              <NXGDSButton
                :size="NXGDSButtonSizes.medium"
                :icon="detailsToggleIcon"
                :variant="NXGDSButtonVariants.outlined"
                :aria-expanded="isDetailsExpanded"
                @click="toggleDetailsExpanded"
              />
            </div>
            <div
              v-if="hasExpandableRows"
              class="ss-details-table__total-expanded"
              :class="{ 'ss-details-table__total-expanded--open': isDetailsExpanded }"
            >
              <div class="ss-details-table__total-expanded-inner">
                <div
                  v-for="(detailRow, index) in footer?.expandableRows"
                  :key="`detail-${index}`"
                  :class="getFooterRowClasses(detailRow)"
                >
                  <div
                    class="ss-details-table__total-row__item"
                    :class="getFooterItemClasses(detailRow.tone)"
                  >
                    {{ t(detailRow.labelKey) }}
                  </div>
                  <div
                    class="ss-details-table__total-row__item"
                    :class="getFooterItemClasses(detailRow.tone)"
                  >
                    {{ detailRow.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins' as mixins;

.ss-details-table {
  width: 100%;
  padding: var(--nxgds-alias-spacing-2xs);
  background: var(--nxgds-mapped-color-surface-component-subsection);
  border-collapse: separate;
  border-spacing: 0 var(--nxgds-alias-spacing-3xs);
  border-radius: var(--nxgds-alias-radius-corner-md);
  color: var(--nxgds-mapped-color-text-secondary);

  &__tbody-row,
  &__tfoot-row {
    color: var(--nxgds-mapped-color-text-primary);
    border-radius: var(--nxgds-alias-radius-corner-md);
    background-color: var(--nxgds-mapped-color-surface-component-card-default);
  }

  &__cell {
    padding: 0 var(--nxgds-alias-spacing-sm);

    &:first-child {
      padding-left: var(--nxgds-alias-spacing-md);
      border-top-left-radius: var(--nxgds-alias-radius-corner-md);
      border-bottom-left-radius: var(--nxgds-alias-radius-corner-md);
    }

    &:last-child {
      padding-right: var(--nxgds-alias-spacing-md);
      border-top-right-radius: var(--nxgds-alias-radius-corner-md);
      border-bottom-right-radius: var(--nxgds-alias-radius-corner-md);
    }

    &--banker {
      width: 1%;
      padding-inline: var(--nxgds-alias-spacing-2xs);
      white-space: nowrap;
    }

    &--align-left {
      text-align: left;
    }

    &--align-right {
      text-align: right;
    }
  }

  &__thead-row &__cell {
    height: 40px;
    @include mixins.typography('label-medium');
  }

  &__tbody-row &__cell {
    height: 52px;
    @include mixins.typography('label-regular');
  }

  &__tfoot-row &__cell {
    padding: var(--nxgds-alias-spacing-md);
    border-radius: var(--nxgds-alias-radius-corner-md);
  }

  &__text-secondary {
    color: var(--nxgds-mapped-color-text-secondary);
  }

  &__text-primary {
    color: var(--nxgds-mapped-color-text-primary);
  }

  &__text-highlight {
    color: var(--nxgds-mapped-color-text-highlight);
  }

  &__total {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--nxgds-alias-spacing-2xs);

    &-row {
      display: flex;
      justify-content: space-between;

      &--emphasis-medium {
        @include mixins.typography('label-medium');
      }

      &--emphasis-regular {
        @include mixins.typography('label-regular');
      }
    }

    &-toggle {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      transform: translate(-50%, -50%);
    }

    &-expanded {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.25s ease;

      &--open {
        grid-template-rows: 1fr;
      }

      &--open &-inner {
        opacity: 1;
      }

      &-inner {
        display: flex;
        flex-direction: column;
        gap: var(--nxgds-alias-spacing-2xs);
        overflow: hidden;
        opacity: 0;
        transition: opacity 0.25s ease;
      }
    }
  }
}
</style>
